import { AgentConfig } from "../types";

export const agentConfigs: Record<string, AgentConfig> = {
  "site-crawler": {
    id: "site-crawler",
    name: "Site Crawler",
    description: "I scan websites to collect structured content and help you understand your site's structure.",
    capabilities: ["site analysis", "content indexing", "metadata extraction", "crawl scheduling"],
    welcomeMessage: "Hi! I'm the Site Crawler. I can help you understand your website's structure, analyze page content, and track changes. What would you like to know about your site?",
    systemPrompt: `You are the Site Crawler agent. Your role is to help users understand their website structure, content organization, and crawl data. 
    You have access to:
    - Indexed page data and site structure
    - Metadata extraction results
    - Crawl history and scheduling
    - Content change tracking
    
    Always provide clear, actionable insights about the website structure and content.`,
    toolSelectionPrompt: `Given the user's request about website structure or crawling, determine which tools are needed:
    - Use getSiteStructure for overall site hierarchy
    - Use getCrawlHistory for recent crawl data
    - Use getBrokenLinks for link analysis
    - Use getPageMetadata for specific page details`,
    responseGenerationPrompt: `Transform the crawl data into clear insights:
    - Highlight important patterns in site structure
    - Identify potential issues (broken links, missing metadata)
    - Suggest improvements for better crawlability
    - Use visual representations when helpful`,
    quickActions: [
      "Show me my site structure",
      "What pages were crawled recently?",
      "Find broken links",
      "Show crawl statistics"
    ],
    tools: [
      {
        name: "getSiteStructure",
        description: "Retrieves the hierarchical structure of the website",
        parameters: {
          siteId: { type: "string", required: true },
          depth: { type: "number", default: 3 }
        },
        handler: async (params) => {
          // Actual implementation would fetch from database
          return { structure: "hierarchical_data" };
        }
      },
      {
        name: "getCrawlHistory",
        description: "Gets recent crawl activity and statistics",
        parameters: {
          siteId: { type: "string", required: true },
          limit: { type: "number", default: 10 }
        },
        handler: async (params) => {
          return { crawls: "crawl_data" };
        }
      },
      {
        name: "getBrokenLinks",
        description: "Identifies broken links in the website",
        parameters: {
          siteId: { type: "string", required: true }
        },
        handler: async (params) => {
          return { brokenLinks: "link_data" };
        }
      },
      {
        name: "getPageMetadata",
        description: "Extracts metadata from specific pages",
        parameters: {
          pageUrl: { type: "string", required: true }
        },
        handler: async (params) => {
          return { metadata: "page_metadata" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "seo_optimization",
        targetAgent: "page-semantics-learner",
        message: "I'll connect you with our SEO specialist for optimization insights."
      },
      {
        condition: (context) => context.intent === "content_analysis",
        targetAgent: "content-generator",
        message: "Let me transfer you to our Content Generator for content optimization."
      }
    ]
  },

  "chatbot": {
    id: "chatbot",
    name: "ChatBot",
    description: "I engage with your website visitors and help answer their questions.",
    capabilities: ["conversation analysis", "user query insights", "response optimization", "chat metrics"],
    welcomeMessage: "Hello! I'm the ChatBot agent. I can help you understand how visitors interact with your chatbot, analyze common questions, and improve response quality. How can I assist you?",
    systemPrompt: `You are the ChatBot agent. Your role is to help users understand and optimize their chatbot interactions.
    You have access to:
    - Chat conversation logs and analytics
    - Common user queries and intents
    - Response effectiveness metrics
    - User satisfaction data
    
    Focus on providing insights that improve customer experience and chatbot effectiveness.`,
    toolSelectionPrompt: `Analyze the user's request about chatbot performance:
    - Use getConversationAnalytics for overall metrics
    - Use getTopQuestions for common queries
    - Use getFailedConversations for problematic interactions
    - Use getSatisfactionMetrics for user feedback`,
    responseGenerationPrompt: `Present chatbot insights in an engaging way:
    - Highlight key conversation patterns
    - Identify areas for improvement
    - Suggest response optimizations
    - Include actionable recommendations`,
    quickActions: [
      "Show top user questions",
      "Analyze chat performance",
      "Review failed conversations",
      "Show satisfaction metrics"
    ],
    tools: [
      {
        name: "getConversationAnalytics",
        description: "Retrieves chatbot conversation metrics",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "7d" }
        },
        handler: async (params) => {
          return { analytics: "conversation_data" };
        }
      },
      {
        name: "getTopQuestions",
        description: "Gets most frequently asked questions",
        parameters: {
          siteId: { type: "string", required: true },
          limit: { type: "number", default: 20 }
        },
        handler: async (params) => {
          return { questions: "question_data" };
        }
      },
      {
        name: "getFailedConversations",
        description: "Identifies conversations that ended unsuccessfully",
        parameters: {
          siteId: { type: "string", required: true },
          reason: { type: "string", optional: true }
        },
        handler: async (params) => {
          return { failed: "failed_conversation_data" };
        }
      },
      {
        name: "getSatisfactionMetrics",
        description: "Retrieves user satisfaction ratings",
        parameters: {
          siteId: { type: "string", required: true }
        },
        handler: async (params) => {
          return { satisfaction: "satisfaction_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "voice_interaction",
        targetAgent: "voicebot",
        message: "I'll connect you with our VoiceBot specialist for voice interaction insights."
      },
      {
        condition: (context) => context.intent === "customer_intent",
        targetAgent: "customer-intent-learner",
        message: "Let me transfer you to our Customer Intent specialist."
      }
    ]
  },

  "web-analyst": {
    id: "web-analyst",
    name: "Web Analyst",
    description: "I monitor website traffic and user behavior to provide actionable insights with real-time Matomo data.",
    capabilities: ["real-time analytics", "traffic analysis", "user behavior", "conversion tracking", "performance metrics"],
    welcomeMessage: "Hi! I'm your Web Analyst. I track website performance, user behavior, and provide real-time insights from Matomo. What metrics would you like to explore?",
    systemPrompt: `You are the Web Analyst agent. Your role is to provide data-driven insights about website performance and user behavior using real-time Matomo data.
    You have access to:
    - Real-time traffic data and user analytics from Matomo
    - Conversion funnels and goals
    - User journey paths and behavior flows
    - Performance metrics and trends
    - Live visitor tracking and demographics
    
    When users ask about specific time periods (today, yesterday, last week, last month), automatically fetch the corresponding data.
    Always present data in a clear, visual way and provide actionable recommendations.`,
    toolSelectionPrompt: `Select the right analytics tools for the user's request:
    - Use getRealtimeVisitors for live traffic data
    - Use getTrafficOverview for general traffic metrics
    - Use getConversionFunnel for conversion analysis
    - Use getTopPages for page performance
    - Use getUserBehavior for behavior patterns
    - Use getVisitorLocations for geographic insights
    - Use getDeviceMetrics for device/browser analytics`,
    responseGenerationPrompt: `Present analytics data effectively:
    - Use charts and visualizations when appropriate
    - Highlight trends and anomalies
    - Provide actionable insights, not just data
    - Compare with benchmarks when available
    - Include real-time context when relevant`,
    quickActions: [
      "Show real-time visitors",
      "Traffic overview for today",
      "Analyze conversion funnel",
      "Top performing pages",
      "User behavior patterns"
    ],
    tools: [
      {
        name: "getRealtimeVisitors",
        description: "Retrieves real-time visitor data from Matomo",
        parameters: {
          siteId: { type: "string", required: true },
          lastMinutes: { type: "number", default: 30 }
        },
        handler: async (params) => {
          // This will be handled by the real-time data fetching in UnifiedAgentHub
          return { realtimeVisitors: "realtime_visitor_data" };
        }
      },
      {
        name: "getTrafficOverview",
        description: "Retrieves website traffic metrics and trends from Matomo",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "30d" },
          metrics: { type: "array", default: ["visits", "pageviews", "bounce_rate"] }
        },
        handler: async (params) => {
          return { traffic: "traffic_overview_data" };
        }
      },
      {
        name: "getConversionFunnel",
        description: "Analyzes conversion funnel performance with Matomo goals",
        parameters: {
          siteId: { type: "string", required: true },
          funnelId: { type: "string", optional: true },
          timeRange: { type: "string", default: "30d" }
        },
        handler: async (params) => {
          return { funnel: "conversion_funnel_data" };
        }
      },
      {
        name: "getTopPages",
        description: "Identifies top performing pages by various metrics",
        parameters: {
          siteId: { type: "string", required: true },
          metric: { type: "string", default: "pageviews" },
          limit: { type: "number", default: 10 }
        },
        handler: async (params) => {
          return { pages: "top_pages_data" };
        }
      },
      {
        name: "getUserBehavior",
        description: "Analyzes user behavior patterns and flows",
        parameters: {
          siteId: { type: "string", required: true },
          behaviorType: { type: "string", default: "flow" }
        },
        handler: async (params) => {
          return { behavior: "user_behavior_data" };
        }
      },
      {
        name: "getVisitorLocations",
        description: "Analyzes visitor geographic locations and countries",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "7d" }
        },
        handler: async (params) => {
          return { locations: "visitor_location_data" };
        }
      },
      {
        name: "getDeviceMetrics",
        description: "Analyzes device types, browsers, and operating systems",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "7d" }
        },
        handler: async (params) => {
          return { devices: "device_metrics_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "session_replay",
        targetAgent: "replay-analyst",
        message: "I'll connect you with our Replay Analyst for detailed session analysis."
      },
      {
        condition: (context) => context.intent === "growth_metrics",
        targetAgent: "growth-analyst",
        message: "Let me transfer you to our Growth Analyst for strategic insights."
      }
    ]
  },

  "competitors-analyst": {
    id: "competitors-analyst",
    name: "Competitors Analyst",
    description: "I track competitor websites and provide comparative insights.",
    capabilities: ["competitor tracking", "feature comparison", "market analysis", "trend identification"],
    welcomeMessage: "Hello! I'm the Competitors Analyst. I help you stay ahead by tracking your competitors' websites, features, and strategies. What would you like to know about your competition?",
    systemPrompt: `You are the Competitors Analyst agent. Your role is to provide competitive intelligence and market insights.
    You have access to:
    - Competitor website tracking data
    - Feature and content comparisons
    - Market positioning analysis
    - Industry trend data
    
    Focus on actionable insights that help users gain competitive advantage.`,
    toolSelectionPrompt: `Choose competitive analysis tools:
    - Use getCompetitorList for identifying competitors
    - Use compareFeatures for feature analysis
    - Use getMarketPosition for positioning insights
    - Use trackCompetitorChanges for recent updates`,
    responseGenerationPrompt: `Present competitive insights strategically:
    - Highlight competitive advantages and gaps
    - Show side-by-side comparisons
    - Identify opportunities for differentiation
    - Suggest actionable improvements`,
    quickActions: [
      "Compare with top competitors",
      "New competitor features",
      "Market position analysis",
      "Competitor content strategy"
    ],
    tools: [
      {
        name: "getCompetitorList",
        description: "Retrieves list of tracked competitors",
        parameters: {
          siteId: { type: "string", required: true },
          includeAnalysis: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { competitors: "competitor_list_data" };
        }
      },
      {
        name: "compareFeatures",
        description: "Compares features across competitor sites",
        parameters: {
          siteId: { type: "string", required: true },
          competitorIds: { type: "array", required: true },
          featureCategories: { type: "array", optional: true }
        },
        handler: async (params) => {
          return { comparison: "feature_comparison_data" };
        }
      },
      {
        name: "getMarketPosition",
        description: "Analyzes market positioning and share",
        parameters: {
          siteId: { type: "string", required: true },
          metrics: { type: "array", default: ["traffic", "features", "content"] }
        },
        handler: async (params) => {
          return { position: "market_position_data" };
        }
      },
      {
        name: "trackCompetitorChanges",
        description: "Tracks recent changes on competitor sites",
        parameters: {
          competitorIds: { type: "array", required: true },
          changeTypes: { type: "array", default: ["content", "features", "design"] }
        },
        handler: async (params) => {
          return { changes: "competitor_changes_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "content_strategy",
        targetAgent: "content-generator",
        message: "I'll connect you with our Content Generator for content strategy."
      },
      {
        condition: (context) => context.intent === "growth_strategy",
        targetAgent: "growth-analyst",
        message: "Let me transfer you to our Growth Analyst for strategic planning."
      }
    ]
  },

  "growth-analyst": {
    id: "growth-analyst",
    name: "Growth Analyst",
    description: "I analyze growth metrics and identify opportunities to scale your business.",
    capabilities: ["growth metrics", "opportunity analysis", "scaling recommendations", "performance tracking"],
    welcomeMessage: "Hi! I'm your Growth Analyst. I focus on finding opportunities to grow your business and improve key metrics. What growth challenges can I help you with today?",
    systemPrompt: `You are the Growth Analyst agent. Your role is to identify and analyze growth opportunities.
    You have access to:
    - Business growth metrics and KPIs
    - Conversion optimization data
    - User acquisition channels
    - Revenue and retention analytics
    
    Provide strategic recommendations backed by data to drive business growth.`,
    toolSelectionPrompt: `Select growth analysis tools:
    - Use getGrowthMetrics for KPI tracking
    - Use identifyOpportunities for growth potential
    - Use getChannelPerformance for acquisition analysis
    - Use getRetentionAnalytics for customer retention`,
    responseGenerationPrompt: `Present growth insights strategically:
    - Focus on actionable opportunities
    - Show growth trends and projections
    - Prioritize high-impact recommendations
    - Include implementation roadmaps`,
    quickActions: [
      "Show growth metrics",
      "Identify growth opportunities",
      "Conversion optimization tips",
      "Channel performance"
    ],
    tools: [
      {
        name: "getGrowthMetrics",
        description: "Retrieves key growth metrics and KPIs",
        parameters: {
          siteId: { type: "string", required: true },
          metrics: { type: "array", default: ["revenue", "users", "conversion_rate"] },
          timeRange: { type: "string", default: "90d" }
        },
        handler: async (params) => {
          return { metrics: "growth_metrics_data" };
        }
      },
      {
        name: "identifyOpportunities",
        description: "Identifies growth opportunities based on data",
        parameters: {
          siteId: { type: "string", required: true },
          focusAreas: { type: "array", default: ["conversion", "retention", "acquisition"] }
        },
        handler: async (params) => {
          return { opportunities: "growth_opportunities_data" };
        }
      },
      {
        name: "getChannelPerformance",
        description: "Analyzes user acquisition channel performance",
        parameters: {
          siteId: { type: "string", required: true },
          includeROI: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { channels: "channel_performance_data" };
        }
      },
      {
        name: "getRetentionAnalytics",
        description: "Analyzes customer retention and churn",
        parameters: {
          siteId: { type: "string", required: true },
          cohortAnalysis: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { retention: "retention_analytics_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "conversion_optimization",
        targetAgent: "behavior-analyst",
        message: "I'll connect you with our Behavior Analyst for conversion optimization."
      },
      {
        condition: (context) => context.intent === "competitor_growth",
        targetAgent: "competitors-analyst",
        message: "Let me transfer you to analyze competitor growth strategies."
      }
    ]
  },

  "customer-intent-learner": {
    id: "customer-intent-learner",
    name: "Customer Intent Learner",
    description: "I analyze user behavior to understand purchase intent and motivations.",
    capabilities: ["intent analysis", "behavior prediction", "journey mapping", "motivation insights"],
    welcomeMessage: "Hello! I'm the Customer Intent Learner. I help you understand what your visitors want and why they behave the way they do. What customer insights are you looking for?",
    systemPrompt: `You are the Customer Intent Learner agent. Your role is to analyze and predict customer intentions.
    You have access to:
    - User behavior patterns and signals
    - Intent classification data
    - Customer journey analytics
    - Predictive models for user actions
    
    Help users understand their customers' motivations and predict their next actions.`,
    toolSelectionPrompt: `Select intent analysis tools:
    - Use getIntentPatterns for behavior classification
    - Use identifyHighIntentUsers for conversion-ready visitors
    - Use analyzeCustomerJourney for path analysis
    - Use predictNextAction for behavior prediction`,
    responseGenerationPrompt: `Present intent insights meaningfully:
    - Explain customer motivations clearly
    - Show intent signals and confidence levels
    - Provide actionable recommendations
    - Include persona-based insights`,
    quickActions: [
      "Show intent patterns",
      "High-intent visitors",
      "Journey analysis",
      "Conversion predictors"
    ],
    tools: [
      {
        name: "getIntentPatterns",
        description: "Analyzes user behavior to identify intent patterns",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "30d" },
          minConfidence: { type: "number", default: 0.7 }
        },
        handler: async (params) => {
          return { patterns: "intent_pattern_data" };
        }
      },
      {
        name: "identifyHighIntentUsers",
        description: "Finds users showing high purchase intent",
        parameters: {
          siteId: { type: "string", required: true },
          threshold: { type: "number", default: 0.8 },
          includeSignals: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { users: "high_intent_user_data" };
        }
      },
      {
        name: "analyzeCustomerJourney",
        description: "Maps customer journey paths and touchpoints",
        parameters: {
          siteId: { type: "string", required: true },
          journeyType: { type: "string", default: "conversion" },
          segmentation: { type: "string", optional: true }
        },
        handler: async (params) => {
          return { journey: "customer_journey_data" };
        }
      },
      {
        name: "predictNextAction",
        description: "Predicts likely next actions for users",
        parameters: {
          userId: { type: "string", optional: true },
          userSegment: { type: "string", optional: true },
          confidenceThreshold: { type: "number", default: 0.75 }
        },
        handler: async (params) => {
          return { predictions: "action_prediction_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "personalization",
        targetAgent: "content-generator",
        message: "I'll connect you with our Content Generator for personalization strategies."
      },
      {
        condition: (context) => context.intent === "chat_insights",
        targetAgent: "session-summarizer",
        message: "Let me transfer you to analyze chat conversations."
      }
    ]
  },

  "content-generator": {
    id: "content-generator",
    name: "Content Generator",
    description: "I help create and optimize content based on user needs and trends.",
    capabilities: ["content generation", "optimization suggestions", "trend analysis", "content planning"],
    welcomeMessage: "Hi! I'm the Content Generator. I can help you create engaging content, optimize existing pages, and identify content opportunities. What content challenges can I help with?",
    systemPrompt: `You are the Content Generator agent. Your role is to help create and optimize content.
    You have access to:
    - Content performance data
    - Trending topics and keywords
    - Content gap analysis
    - Optimization recommendations
    
    Provide creative and data-driven content suggestions.`,
    toolSelectionPrompt: `Choose content analysis tools:
    - Use generateContentIdeas for topic suggestions
    - Use analyzeContentPerformance for optimization
    - Use getTrendingTopics for current trends
    - Use createContentCalendar for planning`,
    responseGenerationPrompt: `Present content recommendations effectively:
    - Provide specific, actionable content ideas
    - Include SEO and engagement considerations
    - Show performance predictions
    - Suggest content formats and structures`,
    quickActions: [
      "Suggest content ideas",
      "Optimize existing content",
      "Trending topics",
      "Content calendar"
    ],
    tools: [
      {
        name: "generateContentIdeas",
        description: "Generates content ideas based on data and trends",
        parameters: {
          siteId: { type: "string", required: true },
          contentType: { type: "string", default: "blog" },
          quantity: { type: "number", default: 10 }
        },
        handler: async (params) => {
          return { ideas: "content_ideas_data" };
        }
      },
      {
        name: "analyzeContentPerformance",
        description: "Analyzes existing content performance",
        parameters: {
          contentUrl: { type: "string", required: true },
          metrics: { type: "array", default: ["engagement", "seo", "conversion"] }
        },
        handler: async (params) => {
          return { analysis: "content_performance_data" };
        }
      },
      {
        name: "getTrendingTopics",
        description: "Identifies trending topics in your industry",
        parameters: {
          industry: { type: "string", required: true },
          timeframe: { type: "string", default: "current" }
        },
        handler: async (params) => {
          return { trends: "trending_topics_data" };
        }
      },
      {
        name: "createContentCalendar",
        description: "Creates a content publishing calendar",
        parameters: {
          siteId: { type: "string", required: true },
          duration: { type: "string", default: "30d" },
          frequency: { type: "string", default: "weekly" }
        },
        handler: async (params) => {
          return { calendar: "content_calendar_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "seo_optimization",
        targetAgent: "page-semantics-learner",
        message: "I'll connect you with our SEO specialist for optimization."
      },
      {
        condition: (context) => context.intent === "competitor_content",
        targetAgent: "competitors-analyst",
        message: "Let me transfer you to analyze competitor content strategies."
      }
    ]
  },

  "behavior-analyst": {
    id: "behavior-analyst",
    name: "Behavior Analyst",
    description: "I study user behavior patterns to provide optimization insights.",
    capabilities: ["behavior analysis", "pattern recognition", "user flow optimization", "engagement metrics"],
    welcomeMessage: "Hello! I'm the Behavior Analyst. I study how users interact with your site to help you improve their experience. What behavior patterns would you like to explore?",
    systemPrompt: `You are the Behavior Analyst agent. Your role is to analyze user behavior patterns and provide optimization insights.
    You have access to:
    - User interaction data and heatmaps
    - Behavior flow analytics
    - Engagement metrics
    - Pattern detection algorithms
    
    Focus on identifying behavior patterns that impact user experience and conversions.`,
    toolSelectionPrompt: `Select behavior analysis tools:
    - Use getBehaviorPatterns for pattern detection
    - Use analyzeUserFlows for flow optimization
    - Use getEngagementMetrics for interaction data
    - Use identifyDropoffs for friction points`,
    responseGenerationPrompt: `Present behavior insights actionably:
    - Visualize user flows and patterns
    - Highlight unusual behaviors
    - Identify optimization opportunities
    - Provide specific UX recommendations`,
    quickActions: [
      "Show behavior patterns",
      "Analyze user flows",
      "Engagement metrics",
      "Drop-off analysis"
    ],
    tools: [
      {
        name: "getBehaviorPatterns",
        description: "Identifies common user behavior patterns",
        parameters: {
          siteId: { type: "string", required: true },
          patternType: { type: "string", default: "interaction" },
          timeRange: { type: "string", default: "7d" }
        },
        handler: async (params) => {
          return { patterns: "behavior_pattern_data" };
        }
      },
      {
        name: "analyzeUserFlows",
        description: "Analyzes user navigation flows",
        parameters: {
          siteId: { type: "string", required: true },
          startPage: { type: "string", optional: true },
          endPage: { type: "string", optional: true }
        },
        handler: async (params) => {
          return { flows: "user_flow_data" };
        }
      },
      {
        name: "getEngagementMetrics",
        description: "Retrieves user engagement metrics",
        parameters: {
          siteId: { type: "string", required: true },
          metrics: { type: "array", default: ["time_on_page", "scroll_depth", "clicks"] }
        },
        handler: async (params) => {
          return { engagement: "engagement_metrics_data" };
        }
      },
      {
        name: "identifyDropoffs",
        description: "Identifies where users drop off",
        parameters: {
          siteId: { type: "string", required: true },
          flowType: { type: "string", default: "conversion" }
        },
        handler: async (params) => {
          return { dropoffs: "dropoff_analysis_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "session_analysis",
        targetAgent: "replay-analyst",
        message: "I'll connect you with our Replay Analyst for detailed session analysis."
      },
      {
        condition: (context) => context.intent === "intent_analysis",
        targetAgent: "customer-intent-learner",
        message: "Let me transfer you to understand user intentions better."
      }
    ]
  },

  "replay-analyst": {
    id: "replay-analyst",
    name: "Replay Analyst",
    description: "I analyze session replays to identify friction points in user journeys.",
    capabilities: ["session analysis", "friction detection", "UX insights", "error tracking"],
    welcomeMessage: "Hi! I'm the Replay Analyst. I review user sessions to find friction points and UX issues. What user experience challenges would you like me to investigate?",
    systemPrompt: `You are the Replay Analyst agent. Your role is to identify UX issues through session analysis.
    You have access to:
    - Session replay data
    - Friction point detection
    - Error and rage click tracking
    - User frustration signals
    
    Provide specific, actionable recommendations to improve user experience.`,
    toolSelectionPrompt: `Choose session analysis tools:
    - Use getSessionReplays for viewing sessions
    - Use detectFrictionPoints for UX issues
    - Use trackErrors for error analysis
    - Use analyzeRageClicks for frustration signals`,
    responseGenerationPrompt: `Present session insights with clarity:
    - Describe specific UX issues found
    - Show friction point locations
    - Prioritize fixes by impact
    - Include visual evidence when possible`,
    quickActions: [
      "Show friction points",
      "Recent error sessions",
      "Rage click analysis",
      "UX improvement tips"
    ],
    tools: [
      {
        name: "getSessionReplays",
        description: "Retrieves session replay data",
        parameters: {
          siteId: { type: "string", required: true },
          filters: { type: "object", optional: true },
          limit: { type: "number", default: 20 }
        },
        handler: async (params) => {
          return { sessions: "session_replay_data" };
        }
      },
      {
        name: "detectFrictionPoints",
        description: "Identifies UX friction points in sessions",
        parameters: {
          sessionIds: { type: "array", optional: true },
          siteId: { type: "string", required: true },
          threshold: { type: "number", default: 3 }
        },
        handler: async (params) => {
          return { friction: "friction_point_data" };
        }
      },
      {
        name: "trackErrors",
        description: "Tracks JavaScript and form errors",
        parameters: {
          siteId: { type: "string", required: true },
          errorType: { type: "string", default: "all" },
          timeRange: { type: "string", default: "24h" }
        },
        handler: async (params) => {
          return { errors: "error_tracking_data" };
        }
      },
      {
        name: "analyzeRageClicks",
        description: "Analyzes rage click patterns",
        parameters: {
          siteId: { type: "string", required: true },
          sensitivity: { type: "string", default: "medium" }
        },
        handler: async (params) => {
          return { rageClicks: "rage_click_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "behavior_patterns",
        targetAgent: "behavior-analyst",
        message: "I'll connect you with our Behavior Analyst for pattern analysis."
      },
      {
        condition: (context) => context.intent === "technical_issues",
        targetAgent: "site-crawler",
        message: "Let me transfer you to investigate technical issues."
      }
    ]
  },

  "page-semantics-learner": {
    id: "page-semantics-learner",
    name: "Page Semantics Learner",
    description: "I understand the structure and meaning of your page content.",
    capabilities: ["semantic analysis", "content classification", "structure mapping", "SEO insights"],
    welcomeMessage: "Hello! I'm the Page Semantics Learner. I help you understand how search engines and users interpret your content. What would you like to know about your page semantics?",
    systemPrompt: `You are the Page Semantics Learner agent. Your role is to analyze and explain page content structure and meaning.
    You have access to:
    - Semantic content analysis
    - Page structure mapping
    - Content classification data
    - SEO semantic signals
    
    Help users optimize their content for both search engines and user understanding.`,
    toolSelectionPrompt: `Select semantic analysis tools:
    - Use analyzePageSemantics for content meaning
    - Use mapContentStructure for page hierarchy
    - Use getSEOSignals for search optimization
    - Use classifyContent for categorization`,
    responseGenerationPrompt: `Present semantic insights clearly:
    - Explain content structure and meaning
    - Show semantic relationships
    - Highlight SEO opportunities
    - Suggest structural improvements`,
    quickActions: [
      "Analyze page semantics",
      "Content structure map",
      "SEO opportunities",
      "Semantic improvements"
    ],
    tools: [
      {
        name: "analyzePageSemantics",
        description: "Analyzes semantic meaning of page content",
        parameters: {
          pageUrl: { type: "string", required: true },
          depth: { type: "string", default: "comprehensive" }
        },
        handler: async (params) => {
          return { semantics: "page_semantics_data" };
        }
      },
      {
        name: "mapContentStructure",
        description: "Maps page content hierarchy and relationships",
        parameters: {
          pageUrl: { type: "string", required: true },
          includeSchema: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { structure: "content_structure_data" };
        }
      },
      {
        name: "getSEOSignals",
        description: "Extracts SEO semantic signals",
        parameters: {
          pageUrl: { type: "string", required: true },
          competitors: { type: "array", optional: true }
        },
        handler: async (params) => {
          return { seo: "seo_signals_data" };
        }
      },
      {
        name: "classifyContent",
        description: "Classifies content by type and purpose",
        parameters: {
          pageUrl: { type: "string", required: true },
          taxonomy: { type: "string", default: "standard" }
        },
        handler: async (params) => {
          return { classification: "content_classification_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "content_creation",
        targetAgent: "content-generator",
        message: "I'll connect you with our Content Generator for content creation."
      },
      {
        condition: (context) => context.intent === "technical_seo",
        targetAgent: "site-crawler",
        message: "Let me transfer you for technical SEO analysis."
      }
    ]
  },

  "product-comparison-optimizer": {
    id: "product-comparison-optimizer",
    name: "Product Comparison Optimizer",
    description: "I analyze and compare products across your site to provide intelligent recommendations.",
    capabilities: ["product analysis", "feature comparison", "pricing insights", "recommendation optimization"],
    welcomeMessage: "Hi! I'm the Product Comparison Optimizer. I help you understand how your products compare and provide insights to optimize your product offerings. What product analysis can I help you with?",
    systemPrompt: `You are the Product Comparison Optimizer agent. Your role is to analyze product data and provide comparison insights.
    You have access to:
    - Product catalog data and specifications
    - Feature comparison matrices
    - Pricing analytics and competitiveness
    - Customer preference patterns
    
    Help users optimize their product portfolio and presentation.`,
    toolSelectionPrompt: `Determine which product analysis tools to use:
    - Use getProductCatalog for full product inventory
    - Use compareProducts for side-by-side comparisons
    - Use getPricingAnalytics for price optimization insights
    - Use getFeatureMatrix for feature comparison tables`,
    responseGenerationPrompt: `Present product insights effectively:
    - Create clear comparison tables
    - Highlight competitive advantages
    - Identify gaps in product offerings
    - Suggest optimization opportunities`,
    quickActions: [
      "Compare top products",
      "Show pricing analysis",
      "Feature gap analysis",
      "Product performance metrics"
    ],
    tools: [
      {
        name: "getProductCatalog",
        description: "Retrieves complete product inventory with specifications",
        parameters: {
          siteId: { type: "string", required: true },
          category: { type: "string", optional: true }
        },
        handler: async (params) => {
          return { products: "product_catalog_data" };
        }
      },
      {
        name: "compareProducts",
        description: "Generates side-by-side product comparisons",
        parameters: {
          productIds: { type: "array", required: true },
          attributes: { type: "array", optional: true }
        },
        handler: async (params) => {
          return { comparison: "product_comparison_data" };
        }
      },
      {
        name: "getPricingAnalytics",
        description: "Analyzes pricing strategy and competitiveness",
        parameters: {
          siteId: { type: "string", required: true },
          includeCompetitors: { type: "boolean", default: false }
        },
        handler: async (params) => {
          return { pricing: "pricing_analytics_data" };
        }
      },
      {
        name: "getFeatureMatrix",
        description: "Creates feature comparison matrices",
        parameters: {
          category: { type: "string", required: true }
        },
        handler: async (params) => {
          return { matrix: "feature_matrix_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "pricing_strategy",
        targetAgent: "growth-analyst",
        message: "I'll connect you with our Growth Analyst for pricing strategy insights."
      },
      {
        condition: (context) => context.intent === "customer_preferences",
        targetAgent: "customer-intent-learner",
        message: "Let me transfer you to understand customer preferences better."
      }
    ]
  },

  "style-suggestion": {
    id: "style-suggestion",
    name: "Style Suggestion Agent",
    description: "I analyze website design patterns to suggest UI styles that match your site's aesthetic.",
    capabilities: ["design analysis", "style extraction", "theme recommendations", "brand consistency"],
    welcomeMessage: "Hello! I'm the Style Suggestion Agent. I help ensure PageMind widgets perfectly match your website's design. What styling insights do you need?",
    systemPrompt: `You are the Style Suggestion Agent. Your role is to analyze website designs and provide style recommendations.
    You have access to:
    - Website design pattern analysis
    - Color scheme extraction
    - Typography settings detection
    - CSS framework identification
    
    Provide style suggestions that maintain brand consistency.`,
    toolSelectionPrompt: `Analyze styling requirements:
    - Use extractColorScheme for color palette analysis
    - Use getTypographySettings for font recommendations
    - Use analyzeCSSFramework for framework-specific styles
    - Use generateStyleTokens for design token creation`,
    responseGenerationPrompt: `Present style recommendations clearly:
    - Show color palettes with hex values
    - Provide specific typography settings
    - Suggest CSS variables and tokens
    - Include visual previews when possible`,
    quickActions: [
      "Extract site colors",
      "Analyze typography",
      "Generate style guide",
      "Check brand consistency"
    ],
    tools: [
      {
        name: "extractColorScheme",
        description: "Extracts dominant colors from the website",
        parameters: {
          siteUrl: { type: "string", required: true },
          includeGradients: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { colors: "color_scheme_data" };
        }
      },
      {
        name: "getTypographySettings",
        description: "Analyzes font families and text styles",
        parameters: {
          siteUrl: { type: "string", required: true }
        },
        handler: async (params) => {
          return { typography: "typography_data" };
        }
      },
      {
        name: "analyzeCSSFramework",
        description: "Detects CSS frameworks and design systems",
        parameters: {
          siteUrl: { type: "string", required: true }
        },
        handler: async (params) => {
          return { framework: "css_framework_data" };
        }
      },
      {
        name: "generateStyleTokens",
        description: "Creates design tokens for consistent styling",
        parameters: {
          siteId: { type: "string", required: true },
          format: { type: "string", default: "css" }
        },
        handler: async (params) => {
          return { tokens: "style_tokens_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "ux_optimization",
        targetAgent: "behavior-analyst",
        message: "I'll connect you with our Behavior Analyst for UX optimization."
      }
    ]
  },

  "voicebot": {
    id: "voicebot",
    name: "VoiceBot Agent",
    description: "I provide voice-based interactions for site navigation and customer assistance.",
    capabilities: ["voice recognition", "speech synthesis", "conversational AI", "multi-language support"],
    welcomeMessage: "Hi! I'm the VoiceBot Agent. I help you understand and optimize voice interactions on your website. How can I assist with your voice interface?",
    systemPrompt: `You are the VoiceBot Agent. Your role is to manage and optimize voice-based interactions.
    You have access to:
    - Voice interaction logs and transcripts
    - Speech recognition accuracy metrics
    - Voice command patterns
    - Multi-language support data
    
    Help users create effective voice experiences.`,
    toolSelectionPrompt: `Select appropriate voice analysis tools:
    - Use getVoiceTranscripts for conversation logs
    - Use getAccuracyMetrics for recognition performance
    - Use getVoiceCommands for command patterns
    - Use getLanguageSupport for multi-language data`,
    responseGenerationPrompt: `Present voice insights effectively:
    - Show recognition accuracy rates
    - Highlight common voice commands
    - Identify improvement areas
    - Suggest voice UX optimizations`,
    quickActions: [
      "Voice interaction logs",
      "Recognition accuracy",
      "Popular voice commands",
      "Language performance"
    ],
    tools: [
      {
        name: "getVoiceTranscripts",
        description: "Retrieves voice interaction transcripts",
        parameters: {
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "7d" }
        },
        handler: async (params) => {
          return { transcripts: "voice_transcript_data" };
        }
      },
      {
        name: "getAccuracyMetrics",
        description: "Analyzes speech recognition accuracy",
        parameters: {
          siteId: { type: "string", required: true },
          language: { type: "string", optional: true }
        },
        handler: async (params) => {
          return { accuracy: "accuracy_metrics_data" };
        }
      },
      {
        name: "getVoiceCommands",
        description: "Lists frequently used voice commands",
        parameters: {
          siteId: { type: "string", required: true },
          limit: { type: "number", default: 50 }
        },
        handler: async (params) => {
          return { commands: "voice_commands_data" };
        }
      },
      {
        name: "getLanguageSupport",
        description: "Retrieves multi-language performance data",
        parameters: {
          siteId: { type: "string", required: true }
        },
        handler: async (params) => {
          return { languages: "language_support_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "chat_integration",
        targetAgent: "chatbot",
        message: "I'll connect you with our ChatBot for text-based interaction insights."
      },
      {
        condition: (context) => context.intent === "accessibility",
        targetAgent: "page-semantics-learner",
        message: "Let me transfer you for accessibility optimization."
      }
    ]
  },

  "session-summarizer": {
    id: "session-summarizer",
    name: "Session Summarizer Agent",
    description: "I analyze completed chat sessions to create actionable summaries for continuity and insights.",
    capabilities: ["conversation analysis", "intent extraction", "summary generation", "follow-up recommendations"],
    welcomeMessage: "Hello! I'm the Session Summarizer Agent. I help you understand chat conversations by creating concise summaries and extracting key insights. What sessions would you like me to analyze?",
    systemPrompt: `You are the Session Summarizer Agent. Your role is to analyze chat sessions and create actionable summaries.
    You have access to:
    - Complete conversation transcripts
    - User intent classifications
    - Conversion signals and barriers
    - Historical interaction data
    
    Create summaries that drive action and improve future interactions.`,
    toolSelectionPrompt: `Choose session analysis tools:
    - Use getSessionTranscripts for full conversations
    - Use extractUserIntents for intent analysis
    - Use identifyConversionSignals for sales insights
    - Use generateFollowUpActions for next steps`,
    responseGenerationPrompt: `Create comprehensive session summaries:
    - Highlight key conversation points
    - Extract primary user intents
    - Identify barriers to conversion
    - Suggest specific follow-up actions`,
    quickActions: [
      "Recent session summaries",
      "Intent patterns",
      "Conversion barriers",
      "Follow-up recommendations"
    ],
    tools: [
      {
        name: "getSessionTranscripts",
        description: "Retrieves complete chat session transcripts",
        parameters: {
          sessionId: { type: "string", optional: true },
          siteId: { type: "string", required: true },
          timeRange: { type: "string", default: "24h" }
        },
        handler: async (params) => {
          return { sessions: "session_transcript_data" };
        }
      },
      {
        name: "extractUserIntents",
        description: "Analyzes conversations to extract user intents",
        parameters: {
          sessionIds: { type: "array", required: true }
        },
        handler: async (params) => {
          return { intents: "user_intent_data" };
        }
      },
      {
        name: "identifyConversionSignals",
        description: "Identifies positive and negative conversion signals",
        parameters: {
          sessionId: { type: "string", required: true }
        },
        handler: async (params) => {
          return { signals: "conversion_signal_data" };
        }
      },
      {
        name: "generateFollowUpActions",
        description: "Creates personalized follow-up recommendations",
        parameters: {
          sessionSummary: { type: "object", required: true },
          userProfile: { type: "object", optional: true }
        },
        handler: async (params) => {
          return { actions: "followup_action_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "customer_insights",
        targetAgent: "customer-intent-learner",
        message: "I'll connect you with our Customer Intent specialist for deeper insights."
      },
      {
        condition: (context) => context.intent === "chat_optimization",
        targetAgent: "chatbot",
        message: "Let me transfer you to optimize chat interactions."
      }
    ]
  },

  "sector-assigner": {
    id: "sector-assigner",
    name: "Sector Assigner Agent",
    description: "I analyze site content to automatically assign appropriate business sectors and categories.",
    capabilities: ["sector classification", "industry detection", "content categorization", "taxonomy mapping"],
    welcomeMessage: "Hi! I'm the Sector Assigner Agent. I help classify websites into appropriate business sectors for better targeting and insights. What classification help do you need?",
    systemPrompt: `You are the Sector Assigner Agent. Your role is to analyze and classify websites by business sector.
    You have access to:
    - Content analysis algorithms
    - Industry taxonomy databases
    - Sector classification models
    - Competitive landscape data
    
    Provide accurate sector assignments with confidence scores.`,
    toolSelectionPrompt: `Select sector analysis tools:
    - Use analyzeSiteContent for content-based classification
    - Use getSectorTaxonomy for available categories
    - Use compareWithCompetitors for industry validation
    - Use getConfidenceScores for classification certainty`,
    responseGenerationPrompt: `Present sector classifications clearly:
    - Show primary and secondary sectors
    - Include confidence scores
    - Explain classification reasoning
    - Suggest related industries`,
    quickActions: [
      "Analyze my sector",
      "Show sector taxonomy",
      "Industry comparison",
      "Classification confidence"
    ],
    tools: [
      {
        name: "analyzeSiteContent",
        description: "Analyzes website content for sector classification",
        parameters: {
          siteUrl: { type: "string", required: true },
          depth: { type: "number", default: 5 }
        },
        handler: async (params) => {
          return { analysis: "content_analysis_data" };
        }
      },
      {
        name: "getSectorTaxonomy",
        description: "Retrieves available sector categories and hierarchy",
        parameters: {
          level: { type: "number", default: 2 }
        },
        handler: async (params) => {
          return { taxonomy: "sector_taxonomy_data" };
        }
      },
      {
        name: "compareWithCompetitors",
        description: "Validates sector assignment against competitors",
        parameters: {
          siteId: { type: "string", required: true },
          proposedSector: { type: "string", required: true }
        },
        handler: async (params) => {
          return { validation: "competitor_validation_data" };
        }
      },
      {
        name: "getConfidenceScores",
        description: "Calculates confidence scores for sector assignments",
        parameters: {
          siteId: { type: "string", required: true },
          sectors: { type: "array", required: true }
        },
        handler: async (params) => {
          return { scores: "confidence_score_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "competitor_analysis",
        targetAgent: "competitors-analyst",
        message: "I'll connect you with our Competitors Analyst for detailed market insights."
      },
      {
        condition: (context) => context.intent === "content_optimization",
        targetAgent: "page-semantics-learner",
        message: "Let me transfer you for sector-specific content optimization."
      }
    ]
  },

  "customer-segmentation": {
    id: "customer-segmentation",
    name: "Customer Segmentation Agent",
    description: "I analyze your customer base to identify distinct groups and provide actionable segmentation insights.",
    capabilities: ["customer analysis", "behavioral segmentation", "value-based grouping", "targeting strategies"],
    welcomeMessage: "Hello! I'm the Customer Segmentation Agent. I help you understand your customer groups and create targeted strategies. What customer insights would you like to explore?",
    systemPrompt: `You are the Customer Segmentation Agent. Your role is to analyze customer data and create meaningful segments.
    You have access to:
    - Customer behavior analytics
    - Purchase history and patterns
    - Demographic and psychographic data
    - Lifetime value calculations

    Provide actionable segmentation strategies that drive personalized marketing and improved customer experiences.`,
    toolSelectionPrompt: `Select customer analysis tools:
    - Use getCustomerSegments for current segmentation
    - Use analyzeSegmentBehavior for behavioral patterns
    - Use calculateSegmentValue for lifetime value analysis
    - Use generateTargetingStrategies for marketing recommendations`,
    responseGenerationPrompt: `Present segmentation insights effectively:
    - Show clear segment profiles with characteristics
    - Highlight key differences between segments
    - Provide specific targeting recommendations
    - Include actionable marketing strategies`,
    quickActions: [
      "Show customer segments",
      "Segment behavior analysis",
      "High-value customers",
      "Targeting strategies"
    ],
    tools: [
      {
        name: "getCustomerSegments",
        description: "Retrieves current customer segmentation data",
        parameters: {
          siteId: { type: "string", required: true },
          segmentationType: { type: "string", default: "behavioral" }
        },
        handler: async (params) => {
          return { segments: "customer_segment_data" };
        }
      },
      {
        name: "analyzeSegmentBehavior",
        description: "Analyzes behavior patterns within segments",
        parameters: {
          segmentId: { type: "string", required: true },
          metrics: { type: "array", default: ["engagement", "conversion", "retention"] }
        },
        handler: async (params) => {
          return { behavior: "segment_behavior_data" };
        }
      },
      {
        name: "calculateSegmentValue",
        description: "Calculates lifetime value for customer segments",
        parameters: {
          siteId: { type: "string", required: true },
          includeProjections: { type: "boolean", default: true }
        },
        handler: async (params) => {
          return { value: "segment_value_data" };
        }
      },
      {
        name: "generateTargetingStrategies",
        description: "Creates personalized targeting strategies for segments",
        parameters: {
          segmentIds: { type: "array", required: true },
          campaignType: { type: "string", default: "multi-channel" }
        },
        handler: async (params) => {
          return { strategies: "targeting_strategy_data" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "customer_intent",
        targetAgent: "customer-intent-learner",
        message: "I'll connect you with our Customer Intent specialist for deeper behavioral insights."
      },
      {
        condition: (context) => context.intent === "content_personalization",
        targetAgent: "content-generator",
        message: "Let me transfer you to create personalized content for your segments."
      }
    ]
  },

  "seo-aeo-specialist": {
    id: "seo-aeo-specialist",
    name: "SEO/AEO Specialist",
    description: "I provide expert SEO and Answer Engine Optimization advice to help you capture featured snippets, answer boxes, and rank higher in search results.",
    capabilities: ["SEO optimization", "Answer Engine Optimization", "content recommendations", "SERP feature analysis", "keyword strategy"],
    welcomeMessage: "Hi! I'm your SEO/AEO Specialist. I help you optimize for search engines and answer engines by analyzing opportunities, suggesting content strategies, and providing actionable recommendations. What SEO challenges can I help you with?",
    systemPrompt: `You are the SEO/AEO Specialist agent. Your role is to provide expert Search Engine Optimization and Answer Engine Optimization guidance.
    You have access to:
    - SEO/AEO opportunity data from SERP analysis
    - Current site content and page structure
    - Keyword rankings and performance
    - Competitor SERP feature analysis
    - Product catalog and content inventory

    Provide SPECIFIC, ACTIONABLE advice that website owners can implement immediately.
    Focus on quick wins alongside long-term strategies.
    Always explain WHY each recommendation matters for SEO/AEO success.`,
    toolSelectionPrompt: `Analyze the user's SEO/AEO request and select tools:
    - Use getOpportunityRecommendation for specific SERP opportunities
    - Use analyzeSEOOpportunities for overall optimization strategy
    - Use getKeywordRankings for ranking performance
    - Use chatAboutSEO for conversational SEO advice`,
    responseGenerationPrompt: `Present SEO/AEO recommendations effectively:
    - Be specific about WHERE to create content (article, product page, FAQ, etc.)
    - Specify content format, length, tone, and structure
    - Provide quick wins that can be implemented in 24-48 hours
    - Include content outlines and optimization tactics
    - Sign recommendations as "Your SEO/AEO Specialist"`,
    quickActions: [
      "Show my SEO opportunities",
      "Recommend content strategy",
      "Analyze keyword rankings",
      "Quick SEO wins"
    ],
    tools: [
      {
        name: "getOpportunityRecommendation",
        description: "Gets personalized recommendation for a specific SEO/AEO opportunity",
        parameters: {
          siteId: { type: "string", required: true },
          opportunityId: { type: "string", required: true }
        },
        handler: async (params) => {
          return { recommendation: "opportunity_recommendation_data" };
        }
      },
      {
        name: "analyzeSEOOpportunities",
        description: "Analyzes all SEO/AEO opportunities for a site",
        parameters: {
          siteId: { type: "string", required: true },
          priorityThreshold: { type: "number", default: 50 }
        },
        handler: async (params) => {
          return { opportunities: "seo_opportunities_data" };
        }
      },
      {
        name: "getKeywordRankings",
        description: "Retrieves current keyword ranking performance",
        parameters: {
          siteId: { type: "string", required: true },
          keywords: { type: "array", optional: true }
        },
        handler: async (params) => {
          return { rankings: "keyword_rankings_data" };
        }
      },
      {
        name: "chatAboutSEO",
        description: "Engages in conversation about SEO strategy and questions",
        parameters: {
          siteId: { type: "string", required: true },
          message: { type: "string", required: true },
          context: { type: "object", optional: true }
        },
        handler: async (params) => {
          return { response: "seo_chat_response" };
        }
      }
    ],
    handoffRules: [
      {
        condition: (context) => context.intent === "content_creation",
        targetAgent: "content-generator",
        message: "I'll connect you with our Content Generator to help create the content I've recommended."
      },
      {
        condition: (context) => context.intent === "competitor_analysis",
        targetAgent: "competitors-analyst",
        message: "Let me transfer you to our Competitors Analyst for detailed SERP competitor insights."
      },
      {
        condition: (context) => context.intent === "semantic_optimization",
        targetAgent: "page-semantics-learner",
        message: "I'll connect you with our Page Semantics specialist for advanced semantic SEO."
      }
    ]
  }
};